import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import DayNavigator, { getISOWeek } from "../../../pages/monteur/DayNavigator";
import {
  StatusFilterChips,
  StatusLegend,
  WerkbonSelectionCard,
} from "../WerkbonViewShared";
import {
  agendaItemOverlapsDay,
  formatDatumTijd,
  isTodayDate,
  toISODate,
} from "../../../pages/monteur/workbonViewUtils";

function resolveLockInfo(item) {
  const explicitLocked = item?.locked === true;
  const executableFrom = item?.executableFrom ? new Date(item.executableFrom) : null;
  const futureLock =
    executableFrom instanceof Date &&
    !Number.isNaN(executableFrom.getTime()) &&
    executableFrom.getTime() > Date.now();

  const locked = explicitLocked || futureLock;
  const lockLabel = locked
    ? `Beschikbaar vanaf ${formatDatumTijd(item?.executableFrom)}`
    : "";

  return { locked, lockLabel };
}

function addDays(baseDate, days) {
  const date = new Date(baseDate);
  date.setDate(date.getDate() + days);
  return date;
}

function startOfWeek(inputDate) {
  const date = new Date(inputDate);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatWeekRange(date) {
  const weekStart = startOfWeek(date);
  const weekEnd = addDays(weekStart, 6);
  const startLabel = weekStart.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit" });
  const endLabel = weekEnd.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit" });
  return `${startLabel} t/m ${endLabel} (Week ${getISOWeek(weekStart)})`;
}

function formatWeekDayLabel(date) {
  return date.toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
  });
}

export default function WerkbonSidebar({
  mobile = false,
  agendaDate,
  setAgendaDate,
  agendaView,
  setAgendaView,
  statusFilter,
  setStatusFilter,
  werkbonSearch,
  setWerkbonSearch,
  navigate,
  werkbonnenLoading,
  werkbonnenError,
  werkbonnenInView,
  activeWerkbonId,
  markedWerkbonId,
  openWerkbon,
}) {
  const location = useLocation();
  const withCurrentSearch = (pathname) => {
    const search = String(location.search || "").trim();
    return search ? `${pathname}${search}` : pathname;
  };
  const containerClassName = mobile
    ? "rounded-xl border border-slate-200 bg-white p-4"
    : "hidden rounded-xl border border-slate-200 bg-white p-3 lg:block lg:h-[calc(100dvh-180px)] lg:overflow-auto";

  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(agendaDate || new Date());
    return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
  }, [agendaDate]);

  const groupedByDay = useMemo(() => {
    const map = {};
    for (const day of weekDays) {
      map[toISODate(day)] = [];
    }

    for (const item of werkbonnenInView || []) {
      let added = false;

      for (const day of weekDays) {
        if (!agendaItemOverlapsDay(item, day)) continue;
        const dayKey = toISODate(day);
        map[dayKey].push(item);
        added = true;
      }

      if (!added) {
        const fallbackKey = toISODate(item?.plannedDate || item?.plannedStartAt);
        if (fallbackKey && map[fallbackKey]) {
          map[fallbackKey].push(item);
        }
      }
    }

    return map;
  }, [werkbonnenInView, weekDays]);

  function shiftWeek(days) {
    setAgendaDate(addDays(agendaDate || new Date(), days));
  }

  return (
    <section className={containerClassName}>
      <div className="mb-3 border-b pb-3">
        <div className="mb-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setAgendaView("day")}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              agendaView === "day" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"
            }`}
          >
            Dag
          </button>
          <button
            type="button"
            onClick={() => setAgendaView("week")}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              agendaView === "week" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"
            }`}
          >
            Week
          </button>
        </div>

        {agendaView === "day" ? (
          <>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Dagweergave
            </p>
            <DayNavigator
              currentDate={agendaDate}
              onDateChange={setAgendaDate}
              compact={!mobile}
              className={mobile ? undefined : "mb-0"}
            />
          </>
        ) : (
          <>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Weekweergave
            </p>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
              <div className="mb-2 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => shiftWeek(-7)}
                  className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-white"
                >
                  &lt;
                </button>
                <p className="text-center text-xs font-semibold text-gray-900">{formatWeekRange(agendaDate || new Date())}</p>
                <button
                  type="button"
                  onClick={() => shiftWeek(7)}
                  className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-white"
                >
                  &gt;
                </button>
              </div>
              <button
                type="button"
                onClick={() => setAgendaDate(new Date())}
                className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
              >
                Deze week
              </button>
            </div>
          </>
        )}
      </div>

      <div className="mb-3 border-b pb-3">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Filters
        </p>
        <StatusLegend compact={!mobile} defaultOpen={false} />
        <StatusFilterChips value={statusFilter} onChange={setStatusFilter} />
        <label className="mt-3 block">
          <span className="sr-only">Zoek werkbonnen</span>
          <input
            type="search"
            value={werkbonSearch}
            onChange={(event) => setWerkbonSearch(event.target.value)}
            placeholder="Zoek op nummer, opdracht of locatie"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>
      </div>

      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Mijn bonnen</h2>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
          {(werkbonnenInView || []).length}
        </span>
      </div>
      <div className="mb-2 flex items-center justify-end">
        <button
          onClick={() => navigate(withCurrentSearch("/monteur/werkbonnen"))}
          className="rounded border px-2 py-1 text-xs hover:bg-gray-50"
        >
          Alles
        </button>
      </div>

      {werkbonnenLoading && <p className="text-sm text-gray-500">Laden...</p>}
      {werkbonnenError && <p className="text-sm text-red-600">{werkbonnenError}</p>}

      {!werkbonnenLoading && !werkbonnenError && agendaView === "day" && (werkbonnenInView || []).length === 0 && (
        <div>
          <p className="text-sm text-gray-500">Geen werkbonnen gepland op deze dag.</p>
          <button
            type="button"
            onClick={() => setAgendaView("week")}
            className="mt-2 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
          >
            Bekijk week
          </button>
        </div>
      )}

      {!werkbonnenLoading && !werkbonnenError && agendaView === "day" && (
        <div className="space-y-2">
          {(werkbonnenInView || []).map((item) => {
            const isOpen = String(item?._id || "") === String(activeWerkbonId || "");
            const isMarkedToday =
              String(item?._id || "") === String(markedWerkbonId || "") &&
              isTodayDate(item?.plannedDate);
            const { locked, lockLabel } = resolveLockInfo(item);

            return (
              <WerkbonSelectionCard
                key={item._id}
                item={item}
                isOpen={isOpen}
                isMarkedToday={isMarkedToday}
                locked={locked}
                lockLabel={lockLabel}
                onClick={() => openWerkbon(item._id, item)}
              />
            );
          })}
        </div>
      )}

      {!werkbonnenLoading && !werkbonnenError && agendaView === "week" && (
        <div className="space-y-3">
          {weekDays.map((day) => {
            const dayKey = toISODate(day);
            const dayItems = groupedByDay[dayKey] || [];

            return (
              <div key={dayKey} className="rounded-lg border border-gray-200 p-2">
                <p className="mb-2 text-xs font-semibold text-gray-700">{formatWeekDayLabel(day)}</p>
                {dayItems.length === 0 && <p className="text-xs text-gray-500">Geen werk gepland</p>}
                {dayItems.map((item) => {
                  const isOpen = String(item?._id || "") === String(activeWerkbonId || "");
                  const isMarkedToday =
                    String(item?._id || "") === String(markedWerkbonId || "") &&
                    isTodayDate(item?.plannedDate);
                  const { locked, lockLabel } = resolveLockInfo(item);

                  return (
                    <WerkbonSelectionCard
                      key={item._id}
                      item={item}
                      isOpen={isOpen}
                      isMarkedToday={isMarkedToday}
                      locked={locked}
                      lockLabel={lockLabel}
                      onClick={() => openWerkbon(item._id, item)}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
